import React from "react";
import PropTypes from "prop-types";
import Downshift from "downshift";

import {
  Wrapper,
  InputIcon,
  List,
  TextHightlight,
  ListItem,
  Required,
  InputWrapper,
  Input,
  Label
} from "./SearchDropdown.styles";

const Item = ({ item, value }) => {
  const regex = new RegExp(`^${value}`, "i");
  const parts = item.text.split(regex);

  if (parts.length === 1) return item.text;

  return parts.map((text, i) => {
    if (i === parts.length - 1) return text;

    return (
      <React.Fragment key={i}>
        {text}
        <TextHightlight>{value}</TextHightlight>
      </React.Fragment>
    );
  });
};

class UISearchDropdown extends React.Component {
  menuButtonNode = null;

  state = {
    placeholder: ""
  };

  constructor(props) {
    super(props);

    if (props.value && props.value.text) {
      this.state = { placeholder: props.value.text };
    }
  }

  setMenuButtonRef = node => {
    !this.menuButtonNode && (this.menuButtonNode = node);
  };

  stateReducer = (state, changes) => {
    if (changes.hasOwnProperty("selectedItem")) {
      this.setState({ placeholder: changes.selectedItem.text });
    }

    const typeIsArrow = [
      Downshift.stateChangeTypes.keyDownArrowUp,
      Downshift.stateChangeTypes.keyDownArrowDown
    ].includes(changes.type);
    const hasNonArrowType = changes.type && !typeIsArrow;

    if (hasNonArrowType || !changes.hasOwnProperty("isOpen")) {
      return changes;
    }

    if (changes.isOpen) {
      return {
        ...changes,
        inputValue: ""
      };
    } else if (state.selectedItem) {
      return {
        ...changes,
        inputValue: state.selectedItem.text
      };
    }

    return changes;
  };

  render() {
    const {
      value,
      items,
      label,
      required,
      readOnly,
      onSelection,
      containerProps,
      ...otherProps
    } = this.props;

    const { placeholder } = this.state;

    return (
      <Downshift
        id={this.props.id}
        selectedItem={value}
        onChange={onSelection}
        itemToString={item => (item ? item.text : "")}
        stateReducer={this.stateReducer}
        defaultHighlightedIndex={0}
      >
        {({
          isOpen,
          innerRef,
          openMenu,
          inputValue,
          toggleMenu,
          highlightedIndex,
          getMenuProps,
          getItemProps,
          getRootProps,
          getInputProps,
          getLabelProps
        }) => (
          <Wrapper
            innerRef={innerRef}
            {...getRootProps({ ...containerProps, refKey: "innerRef" })}
          >
            <Label {...getLabelProps({ className: "search-dropdown__label" })}>
              {label}
              <Required required={required} />
              <InputWrapper className="search-dropdown__input-wrapper">
                <Input
                  readOnly={readOnly}
                  {...getInputProps({
                    ...otherProps,
                    className: "search-dropdown__input",
                    placeholder,
                    onFocus: event =>
                      event.relatedTarget !== this.menuButtonNode && openMenu()
                  })}
                  autoComplete="nope"
                />
                <InputIcon
                  className="dropdown-icon"
                  innerRef={this.setMenuButtonRef}
                  onClick={() => toggleMenu()}
                  tabIndex="-1"
                  show={isOpen}
                />
              </InputWrapper>
            </Label>

            <List
              {...getMenuProps({
                tabIndex: "-1",
                show: isOpen,
                "data-dropdown-list": true,
                className: "dropdown-options"
              })}
            >
              {readOnly
                ? items.map((item, index) => (
                    <ListItem
                      {...getItemProps({
                        key: item.value,
                        item,
                        index,
                        active: highlightedIndex === index,
                        className: "dropdown-option-item"
                      })}
                    >
                      <Item value={inputValue} item={item} />
                    </ListItem>
                  ))
                : items
                    .filter(x =>
                      x.text.toLowerCase().startsWith(inputValue.toLowerCase())
                    )
                    .map((item, index) => (
                      <ListItem
                        {...getItemProps({
                          key: item.value,
                          item,
                          index,
                          active: highlightedIndex === index,
                          className: "dropdown-option-item"
                        })}
                      >
                        <Item value={inputValue} item={item} />
                      </ListItem>
                    ))}
            </List>
          </Wrapper>
        )}
      </Downshift>
    );
  }
}

UISearchDropdown.propTypes = {
  label: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      text: PropTypes.string
    })
  ),
  onSelection: PropTypes.func,
  required: PropTypes.bool
};

UISearchDropdown.defaultProps = {
  items: [],
  onSelection: () => {},
  required: false
};

export default UISearchDropdown;
